import { environment } from 'src/environments/environment';
import {
  resolveAutomateAppUrl,
  resolveCustodianAppUrl,
  resolveHealthpayAppUrl,
  resolveThreadsAppUrl,
} from '../utils/aya-deploy-urls';

export class Utils {
  static parseJwt(token: string): any {
    let base64Url = token.split('.')[1];
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    let jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  }

  static getTokenExpireDate(exp: number) {
    let expiresDate = new Date(0);
    expiresDate.setUTCMilliseconds(exp * 1000);

    return expiresDate;
  }

  /** Basic email check used by deep-link login (trim before calling). */
  static isValidEmail(email: string): boolean {
    if (!email) {
      return false;
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * Entra SMS MFA expects: +(country code) {number}[x{extension}]
   * e.g. +1 5555551234 or +92 3155432768 (space after country code).
   */
  static normalizePhoneForEntra(phone: string): string {
    let value = phone.trim();
    if (!value) {
      return value;
    }

    let extension = '';
    const extMatch = value.match(/x(\d+)\s*$/i);
    if (extMatch && extMatch.index !== undefined) {
      extension = `x${extMatch[1]}`;
      value = value.slice(0, extMatch.index).trim();
    }

    if (/^\+\d{1,3}\s+\S/.test(value)) {
      const formatted = value.replace(/\s+/g, ' ').trim();
      return extension ? `${formatted}${extension}` : formatted;
    }

    const compact = value.replace(/[^\d+]/g, '');
    if (!compact.startsWith('+')) {
      return value;
    }

    const national = compact.slice(1);
    const countryCodes = [
      '886', '880', '977', '971', '966', '92', '91', '86', '84', '82', '81',
      '66', '65', '64', '63', '62', '61', '60', '57', '55', '52', '49', '48',
      '47', '46', '45', '44', '43', '41', '39', '34', '33', '32', '31', '27',
      '20', '1',
    ];

    for (const code of countryCodes) {
      if (national.startsWith(code) && national.length > code.length + 5) {
        const rest = national.slice(code.length);
        const formatted = `+${code} ${rest}`;
        return extension ? `${formatted}${extension}` : formatted;
      }
    }

    if (national.length >= 10) {
      const formatted = `+${national.slice(0, 2)} ${national.slice(2)}`;
      return extension ? `${formatted}${extension}` : formatted;
    }

    return value;
  }

  /**
   * Validates returnUrl for open-redirect safety.
   * Only HTTPS URLs on trusted hosts are accepted:
   * - healthexam.ai / healthexam.com (and subdomains)
   * - lovable.app (and subdomains) — e.g. Aya / Lovable preview apps
   * - a.run.app (and subdomains) — QA Cloud Run deployments
   * - http://qa-*.healthexam.ai / healthexam.com — QA stack (no TLS on some hosts)
   * - http://localhost / http://127.0.0.1 (non-production only)
   * - https://localhost / https://127.0.0.1 (non-production only — local SSL dev server)
   * Returns the normalized href, or null if rejected.
   */
  static sanitizeReturnUrl(returnUrl: string): string | null {
    if (!returnUrl) {
      return null;
    }
    const trimmed = returnUrl.trim();
    if (!trimmed || /^\s*javascript:/i.test(trimmed)) {
      return null;
    }

    let parsed: URL;
    try {
      parsed = new URL(trimmed);
    } catch {
      return null;
    }

    if (parsed.username || parsed.password) {
      return null;
    }

    const host = parsed.hostname.toLowerCase();
    const isLocalDevHost = host === 'localhost' || host === '127.0.0.1';
    if (
      !environment.production &&
      isLocalDevHost &&
      (parsed.protocol === 'http:' || parsed.protocol === 'https:')
    ) {
      return parsed.href;
    }

    const isQaHealthexamHost =
      host.startsWith('qa-') &&
      (host.endsWith('.healthexam.ai') || host.endsWith('.healthexam.com'));
    if (parsed.protocol === 'http:' && isQaHealthexamHost) {
      return parsed.href;
    }

    if (parsed.protocol !== 'https:') {
      return null;
    }

    const isTrustedHost =
      host === 'healthexam.ai' ||
      host.endsWith('.healthexam.ai') ||
      host === 'healthexam.com' ||
      host.endsWith('.healthexam.com') ||
      host === 'lovable.app' ||
      host.endsWith('.lovable.app') ||
      host.endsWith('.a.run.app');

    if (!isTrustedHost) {
      return null;
    }

    return parsed.href;
  }

  /** True when returnUrl targets a Lovable / Aya preview host. */
  static isLovableReturnUrl(returnUrl: string): boolean {
    try {
      const host = new URL(returnUrl).hostname.toLowerCase();
      return host === 'lovable.app' || host.endsWith('.lovable.app');
    } catch {
      return false;
    }
  }

  /** Legacy Angular product SPAs (admin, clinic, biller, …) receive auth at /app-redirect. */
  static isAngularProductHandoffUrl(returnUrl: string): boolean {
    if (Utils.isHealthPayHandoffUrl(returnUrl) || Utils.isThreadsHandoffUrl(returnUrl)) {
      return false;
    }
    if (Utils.isAutomateHandoffUrl(returnUrl)) {
      return false;
    }
    if (Utils.isCustodianHandoffUrl(returnUrl)) {
      return false;
    }
    const sanitized = Utils.sanitizeReturnUrl(returnUrl);
    if (!sanitized) {
      return false;
    }
    try {
      const path = new URL(sanitized).pathname.replace(/\/+$/, '');
      return path.endsWith('/app-redirect');
    } catch {
      return /\/app-redirect(?:\?|$)/i.test(sanitized);
    }
  }

  /** True for dev / qa / prod Ask Aya React app hosts. */
  private static isAyaReactAppHost(host: string): boolean {
    const h = host.toLowerCase();
    return (
      h === 'aya.healthexam.ai' ||
      /^(dev|qa)-aya\.healthexam\.ai$/i.test(h)
    );
  }

  /** True for dev / qa / prod www / health-chat entry hosts. */
  private static isWwwReactAppHost(host: string): boolean {
    const h = host.toLowerCase();
    return (
      h === 'www.healthexam.com' ||
      h === 'www.healthexam.ai' ||
      h === 'healthexam.ai' ||
      /^(dev|qa)-www\.healthexam\.(ai|com)$/i.test(h)
    );
  }

  /**
   * React Aya apps (aya.healthexam.ai, dev-aya, qa-aya, Lovable previews) expect ?token= handoff,
   * not legacy encrypted ?q= blobs used by older Angular product SPAs.
   */
  static isReactAyaReturnUrl(returnUrl: string): boolean {
    if (Utils.isLovableReturnUrl(returnUrl)) {
      return true;
    }
    if (Utils.isAngularProductHandoffUrl(returnUrl)) {
      return false;
    }
    try {
      const host = new URL(returnUrl).hostname.toLowerCase();
      if (Utils.isAyaReactAppHost(host) || Utils.isWwwReactAppHost(host)) {
        return true;
      }
      if (host.endsWith('.a.run.app')) {
        return true;
      }
      if (
        !environment.production &&
        (host === 'localhost' || host === '127.0.0.1')
      ) {
        return true;
      }
    } catch {
      return false;
    }
    return false;
  }

  private static isReactProductHandoffUrl(
    returnUrl: string,
    configuredAppUrl: string,
  ): boolean {
    const sanitized = Utils.sanitizeReturnUrl(returnUrl);
    if (!sanitized) {
      return false;
    }
    try {
      const target = new URL(sanitized);
      const configured = new URL(
        `${(configuredAppUrl || '').trim().replace(/\/+$/, '')}/`,
      );
      return target.origin === configured.origin;
    } catch {
      return false;
    }
  }

  /** Health Pay (TanStack / Vite) — readable ?token= handoff like other React SPAs. */
  static isHealthPayHandoffUrl(returnUrl: string): boolean {
    return Utils.isReactProductHandoffUrl(
      returnUrl,
      resolveHealthpayAppUrl(returnUrl),
    );
  }

  /** Threads (Vite) — readable ?token= handoff like Health Pay. */
  static isThreadsHandoffUrl(returnUrl: string): boolean {
    return Utils.isReactProductHandoffUrl(
      returnUrl,
      resolveThreadsAppUrl(returnUrl),
    );
  }

  /** Aya Automate (Vite) — readable ?token= handoff like Health Pay. */
  static isAutomateHandoffUrl(returnUrl: string): boolean {
    return Utils.isReactProductHandoffUrl(
      returnUrl,
      resolveAutomateAppUrl(returnUrl),
    );
  }

  /** Custodian (Vite) — readable ?token= handoff like Health Pay. */
  static isCustodianHandoffUrl(returnUrl: string): boolean {
    return Utils.isReactProductHandoffUrl(
      returnUrl,
      resolveCustodianAppUrl(returnUrl),
    );
  }

  /**
   * Redirect sends plain `token` / `email` query params (not encrypted `q`).
   * Used for Lovable apps, dev-aya / Aya React hosts, Health Pay, and local Vite during dev.
   */
  static usesReadableTokenReturnUrl(returnUrl: string): boolean {
    if (Utils.isHealthPayHandoffUrl(returnUrl) || Utils.isThreadsHandoffUrl(returnUrl)) {
      return true;
    }
    if (Utils.isAutomateHandoffUrl(returnUrl)) {
      return true;
    }
    if (Utils.isCustodianHandoffUrl(returnUrl)) {
      return true;
    }
    if (Utils.isAngularProductHandoffUrl(returnUrl)) {
      return false;
    }
    if (Utils.isReactAyaReturnUrl(returnUrl)) {
      return true;
    }
    if (environment.production) {
      return false;
    }
    try {
      const host = new URL(returnUrl).hostname.toLowerCase();
      return host === 'localhost' || host === '127.0.0.1';
    } catch {
      return false;
    }
  }

  /**
   * True when handoff targets a consumer Aya / www app that requires terms
   * acceptance (dev-aya, qa-aya, dev-www, qa-www, Lovable previews, etc.).
   */
  static isAyaAppHandoffUrl(returnUrl: string): boolean {
    const sanitized = Utils.sanitizeReturnUrl(returnUrl);
    if (!sanitized) {
      return false;
    }

    if (Utils.isLovableReturnUrl(sanitized)) {
      return true;
    }

    try {
      const parsed = new URL(sanitized);
      const host = parsed.hostname.toLowerCase();

      for (const raw of [
        environment.ayaAppUrl,
        environment.ayaHealthChatAppUrl,
      ]) {
        const configured = Utils.sanitizeReturnUrl(raw?.trim() || '');
        if (configured && new URL(configured).origin === parsed.origin) {
          return true;
        }
      }

      if (Utils.isAyaReactAppHost(host) || Utils.isWwwReactAppHost(host)) {
        return true;
      }
      if (host.endsWith('.a.run.app')) {
        return true;
      }

      if (!environment.production) {
        if (host === 'localhost' || host === '127.0.0.1') {
          const port = parsed.port;
          if (!port || port === '8083' || port === '8080' || port === '8081') {
            return true;
          }
        }
      }
    } catch {
      return false;
    }

    return false;
  }
}

/** Marketing pages allowed to tag first-time signup origin. */
export type SignupSource = 'providers' | 'www' | 'aya';

export class SignupSourceUtil {
  private static readonly allowed: SignupSource[] = ['providers', 'www', 'aya'];

  static sanitize(raw: string | null | undefined): SignupSource | null {
    if (!raw) {
      return null;
    }
    const normalized = String(raw).trim().toLowerCase();
    return SignupSourceUtil.allowed.includes(normalized as SignupSource)
      ? (normalized as SignupSource)
      : null;
  }
}
